import { ArrowRightAlt } from "@ellaandil/ui";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { Typography } from "@/components/ui/Typography";
import type { AssetStoryblok } from "@/generated/sb/sb-types";
import { Link as NextLink } from "@/i18n/routing";
import { Asset } from "./Asset";
import { Skeleton } from "./Skeleton";

export interface CategoryCardProps {
	asset?: AssetStoryblok;
	title?: string;
	caption?: string;
	linkLabel?: string;
	href: string;
	className?: string;
}

export const CategoryCard = forwardRef<HTMLAnchorElement, CategoryCardProps>(
	({ asset, title, caption, linkLabel = "Explore", href, className }, ref) => {
		return (
			<NextLink
				ref={ref}
				href={href}
				className={twMerge(
					"group relative flex aspect-2/3 w-full items-start overflow-hidden bg-background-bg-image text-text-sand-text-2",
					className,
				)}
			>
				<Asset
					asset={asset}
					sizes="(max-width: 640px) 80vw, (max-width: 1024px) 33vw, 25vw"
					className="pointer-events-none object-cover"
				/>
				<div
					aria-hidden
					className="absolute inset-0 bg-overlay-2 transition-colors duration-200 ease-out group-hover:bg-neutral-transparent-black-25"
				/>
				<div className="absolute inset-0 flex flex-col items-start justify-between p-grid-component-spacing-md">
					{caption ? (
						<Typography variant="label-md-reg" className="w-full leading-[1.15]">
							{caption}
						</Typography>
					) : (
						<span aria-hidden />
					)}
					<Typography as="h3" variant="heading-md" className="w-full leading-[1.25]">
						{title}
					</Typography>
					<div className="flex w-full items-center justify-center gap-grid-component-spacing-4xs">
						<Typography variant="label-md-reg" as="span" className="min-w-px flex-1 leading-[1.15]">
							{linkLabel}
						</Typography>
						<div className="size-6 shrink-0 overflow-hidden">
							<div className="pointer-events-none flex -translate-x-full transition-transform duration-500 ease-reveal md:group-hover:translate-x-0">
								<ArrowRightAlt className="size-6 shrink-0" />
								<ArrowRightAlt className="size-6 shrink-0" />
							</div>
						</div>
					</div>
				</div>
			</NextLink>
		);
	},
);

export const CategoryCardSkeleton = () => {
	return (
		<div
			className={twMerge(
				"group relative flex aspect-2/3 w-full items-start overflow-hidden  text-text-sand-text-2",
			)}
		>
			<Skeleton className="h-full w-full" />

			<div className="absolute inset-0 flex flex-col items-start justify-between p-grid-component-spacing-md">
				<Skeleton fitContent>
					<Typography variant="label-md-reg" className="w-full leading-[1.15]">
						Lorem, ipsum.
					</Typography>
				</Skeleton>

				<Skeleton fitContent>
					<Typography as="h3" variant="heading-md" className="w-full leading-[1.25]">
						Lorem, ipsum dolor.
					</Typography>
				</Skeleton>
				<div className="flex w-full items-center justify-center gap-grid-component-spacing-4xs">
					<Skeleton fitContent>
						<Typography variant="label-md-reg" as="span" className="min-w-px flex-1 leading-[1.15]">
							lorem
						</Typography>
					</Skeleton>
					<Skeleton fitContent>
						<ArrowRightAlt className="size-6 shrink-0" />
					</Skeleton>
				</div>
			</div>
		</div>
	);
};

CategoryCard.displayName = "CategoryCard";
